/**
 * AttachmentUploadService
 * 
 * Service responsible for managing file attachments throughout the MacSafety web application.
 * Handles file uploads, validation, processing, and preparation for form submission.
 * 
 * Key Features:
 * - File type and size validation
 * - Image resizing and optimization
 * - Attachment storage and management
 * - File conversion for submission
 * - Signature management integration
 * 
 * Components that utilize this service:
 * - AttachmentUploaderComponent: Main file upload interface
 * - AttachmentViewerModalComponent: Modal for viewing attachments
 * - AttachmentGridViewerComponent: Grid display of attachments
 * - Individual form components: Any form that includes attachment upload functionality
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Injectable, signal, computed } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { EntraService } from './entra.service';
import { ToastService } from './toast.service';
import { AttachmentUploadData, AttachmentDataForSubmission } from '../models/forms/attachments/attachment-upload';
import { ModalController } from '@ionic/angular';
import { AttachmentViewerModalComponent } from '../components/forms/form-submission/attachment-upload-section/attachment-viewer-modal/attachment-viewer-modal.component';
import { SignatureService } from './signature.service';
import { FormAttachmentUploadRequirements } from '../models/forms/attachments/form-attachment-requirements';
import { attachmentDataToFile } from '../utils/create-files';

@Injectable({
  providedIn: 'root'
})
export class AttachmentUploadService {
  // Signal for storing attachments organized by identifier
  private readonly _attachments = signal<Record<string, AttachmentUploadData[]>>({});
  
  // Signal for tracking loading state during file processing
  private readonly _isLoading = signal<boolean>(false);
  
  // Signal for displaying loading messages to users
  private readonly _loadingMessage = signal<string>('');
  
  // Signal for storing attachment requirements for form validation
  private readonly _attachmentRequirements = signal<FormAttachmentUploadRequirements | null>(null);

  // File size limits (in bytes) for different file categories
  private readonly MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB
  private readonly MAX_VIDEO_SIZE = 100 * 1024 * 1024; // 100MB
  private readonly MAX_DOCUMENT_SIZE = 25 * 1024 * 1024; // 25MB

  constructor(
    private http: HttpClient,
    private entraService: EntraService,
    private toastService: ToastService,
    private modalCtrl: ModalController,
    private signatureService: SignatureService
  ) {}

  // Public readonly signals for components to access
  public attachments = this._attachments.asReadonly();
  public isLoading = this._isLoading.asReadonly();
  public loadingMessage = this._loadingMessage.asReadonly();

  /**
   * Sets the attachment requirements for form validation
   * @param requirements - The attachment requirements configuration
   */
  public setAttachmentRequirements(requirements: FormAttachmentUploadRequirements) {
    this._attachmentRequirements.set(requirements);
  }

  /**
   * Computed signal that determines if all required attachments are present
   * Returns true if all requirements are met, false otherwise
   */
  public readonly areAttachmentsValid = computed(() => {
    const requirements = this._attachmentRequirements();
    if (!requirements || requirements.uploads?.length === 0) return true;

    const currentAttachments = this._attachments();
    for (const requirement of requirements.uploads || []) {
      const attachments = currentAttachments[requirement.identifier] || [];

      if (attachments.length < requirement.required && requirement.required !== 0) {
        return false;
      }
    }
    return true;
  });

  /**
   * Returns an array of validation error messages for missing attachments
   * @returns Array of error messages describing which attachments are missing
   */
  public getAttachmentValidationErrors(): Array<string> {
    const errors: Array<string> = [];
    const requirements = this._attachmentRequirements();
    if (!requirements) return errors;

    const currentAttachments = this._attachments();
    for (const requirement of requirements.uploads || []) {
      const attachments = currentAttachments[requirement.identifier] || [];
      if (attachments.length < requirement.required) {
        errors.push(`${requirement.label} (${attachments.length}/${requirement.required})`);
      }
    }
    return errors;
  }

  /**
   * Updates the attachments for a specific identifier
   * @param identifier - The identifier for the attachment group
   * @param attachments - Array of attachment data to set
   */
  public updateAttachments(identifier: string, attachments: AttachmentUploadData[]) {
    this._attachments.update(current => ({
      ...current,
      [identifier]: attachments
    }));
  }

  /**
   * Removes all attachments for a specific identifier
   * @param identifier - The identifier for the attachment group to clear
   */
  public clearAttachments(identifier: string) {
    this._attachments.update(current => {
      const { [identifier]: _, ...rest } = current;
      return rest;
    });
  }

  /**
   * Removes all attachments from all identifiers
   */
  public clearAllAttachments() {
    this._attachments.set({});
  }

  /**
   * Retrieves attachments for a specific identifier
   * @param identifier - The identifier for the attachment group
   * @returns Array of attachment data
   */
  public getAttachments(identifier: string): AttachmentUploadData[] {
    return this._attachments()[identifier] || [];
  }

  /**
   * Checks if the required number of uploads are present for a specific identifier
   * @param identifier - The identifier for the attachment group
   * @returns True if requirements are met, false otherwise
   */
  public hasUploads(identifier: string): boolean {
    const attachments = this._attachments()[identifier] || [];
    const requirement = this._attachmentRequirements()?.uploads?.find(upload => upload.identifier === identifier);
    return attachments.length >= (requirement?.required || 0);
  }

  /**
   * Validates if a file type is supported by the application
   * @param file - The file to validate
   * @returns True if file type is valid, false otherwise
   */
  private isValidFileType(file: File): boolean {
    const validTypes = [
      // Images
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/bmp',
      // Videos
      'video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv', 'video/webm', 'video/mkv',
      // Documents
      'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain', 'text/rtf'
    ];
    
    // Check MIME type first
    if (validTypes.includes(file.type)) {
      return true;
    }
    
    // Fallback: check file extension for common video formats
    const fileName = file.name.toLowerCase();
    const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv'];
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
    const documentExtensions = ['.pdf', '.doc', '.docx', '.txt', '.rtf'];
    
    const allExtensions = [...videoExtensions, ...imageExtensions, ...documentExtensions];
    
    return allExtensions.some(ext => fileName.endsWith(ext));
  }

  /**
   * Validates if a file size is within acceptable limits
   * @param file - The file to validate
   * @returns True if file size is valid, false otherwise
   */
  private isValidFileSize(file: File): boolean {
    if (file.type.startsWith('image/')) {
      return file.size <= this.MAX_IMAGE_SIZE;
    } else if (file.type.startsWith('video/')) {
      return file.size <= this.MAX_VIDEO_SIZE;
    } else {
      return file.size <= this.MAX_DOCUMENT_SIZE;
    }
  }

  /**
   * Determines the category of a file (image, video, or document)
   * @param file - The file to categorize
   * @returns The file category as a string
   */
  private getFileCategory(file: File): 'image' | 'video' | 'document' {
    // Check MIME type first
    if (file.type.startsWith('image/')) {
      return 'image';
    } else if (file.type.startsWith('video/')) {
      return 'video';
    }
    
    // Fallback: check file extension if MIME type is not reliable
    const fileName = file.name.toLowerCase();
    const videoExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv'];
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'];
    
    if (videoExtensions.some(ext => fileName.endsWith(ext))) {
      return 'video';
    } else if (imageExtensions.some(ext => fileName.endsWith(ext))) {
      return 'image';
    } else {
      return 'document';
    }
  }

  /**
   * Formats file size in bytes to human-readable format
   * @param bytes - File size in bytes
   * @returns Formatted file size string (e.g., "2.5 MB")
   */
  private formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Resizes an image file to specified dimensions while maintaining aspect ratio
   * @param file - The image file to resize
   * @param maxWidth - Maximum width in pixels (default: 1920)
   * @param maxHeight - Maximum height in pixels (default: 1080)
   * @param quality - JPEG quality (0-1, default: 0.8)
   * @returns Promise resolving to AttachmentUploadData with resized image
   */
  private async resizeImage(
    file: File,
    maxWidth: number = 1920,
    maxHeight: number = 1080,
    quality: number = 0.8
  ): Promise<AttachmentUploadData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // Calculate new dimensions while maintaining aspect ratio
          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, width, height);

          // Convert to data URL with specified quality
          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          
          const newAttachment: AttachmentUploadData = {
            id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
            url: dataUrl,
            name: file.name,
            type: file.type,
            size: file.size,
            uploadDate: new Date()
          };
          
          resolve(newAttachment);
        };

        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };

      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Processes a video file and converts it to AttachmentUploadData
   * @param file - The video file to process
   * @returns Promise resolving to AttachmentUploadData with video data
   */
  private async processVideo(file: File): Promise<AttachmentUploadData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        
        const newAttachment: AttachmentUploadData = {
          id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
          url: dataUrl,
          name: file.name,
          type: file.type,
          size: file.size,
          uploadDate: new Date()
        };
        
        resolve(newAttachment);
      };

      reader.onerror = () => reject(new Error('Failed to read video file'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Processes a document file and converts it to AttachmentUploadData
   * @param file - The document file to process
   * @returns Promise resolving to AttachmentUploadData with document data
   */
  private async processDocument(file: File): Promise<AttachmentUploadData> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        
        const newAttachment: AttachmentUploadData = {
          id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
          url: dataUrl,
          name: file.name,
          type: file.type,
          size: file.size,
          uploadDate: new Date()
        };
        
        resolve(newAttachment);
      };

      reader.onerror = () => reject(new Error('Failed to read document'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Main method for processing uploaded files
   * Validates, processes, and stores file attachments
   * @param file - The file to process
   * @param identifier - The identifier for the attachment group
   */
  public async processFile(file: File, identifier: string): Promise<void> {
    this._isLoading.set(true);
    
    try {
      // Validate file type
      if (!this.isValidFileType(file)) {
        this.toastService.showWarningMessage(`Invalid file type: ${file.type}. Please upload a supported file type.`);
        return;
      }

      // Validate file size
      if (!this.isValidFileSize(file)) {
        const category = this.getFileCategory(file);
        const maxSize = category === 'image' ? this.MAX_IMAGE_SIZE : 
                       category === 'video' ? this.MAX_VIDEO_SIZE : 
                       this.MAX_DOCUMENT_SIZE;
        
        this.toastService.showWarningMessage(
          `File too large. Maximum size for ${category}s is ${this.formatFileSize(maxSize)}.`
        );
        return;
      }

      const category = this.getFileCategory(file);
      this._loadingMessage.set(`Processing ${category}...`);

      let newAttachment: AttachmentUploadData;

      // Process file based on type
      switch (category) {
        case 'image':
          newAttachment = await this.resizeImage(file);
          break;
        case 'video':
          newAttachment = await this.processVideo(file);
          break;
        case 'document':
          newAttachment = await this.processDocument(file);
          break;
        default:
          throw new Error('Unsupported file category');
      }

      const currentAttachments = this.getAttachments(identifier);
      this.updateAttachments(identifier, [...currentAttachments, newAttachment]);
      
      
    } catch (error) {

      this.toastService.showWarningMessage('Error processing file. Please try again.');
    } finally {
      this._isLoading.set(false);
      this._loadingMessage.set('');
    }
  }

  /**
   * Removes a specific attachment from a group
   * @param identifier - The identifier for the attachment group
   * @param attachmentId - The ID of the attachment to remove
   */
  public deleteAttachment(identifier: string, attachmentId: string): void {
    const currentAttachments = this.getAttachments(identifier);
    this.updateAttachments(
      identifier,
      currentAttachments.filter(attachment => attachment.id !== attachmentId)
    );
  }

  /**
   * Removes all attachments from a specific group
   * @param identifier - The identifier for the attachment group
   */
  public deleteAllAttachments(identifier: string): void {
    this.clearAttachments(identifier);
  }

  /**
   * Prepares attachments for form submission by extracting base64 data
   * @returns Promise resolving to record of attachment data organized by identifier
   */
  public async prepareAttachmentsForSubmission(): Promise<Record<string, AttachmentDataForSubmission[]>> {
    const allAttachments = this._attachments();
    const attachmentUploads: Record<string, AttachmentDataForSubmission[]> = {};

    for (const [identifier, attachments] of Object.entries(allAttachments)) {
      const uploads = await Promise.all(attachments.map(async attachment => {
        const base64Data = attachment.url.split(',')[1];
        return {
          base64Data: base64Data,
          fileName: attachment.name,
          contentType: attachment.type,
          description: identifier
        };
      }));
      attachmentUploads[identifier] = uploads;
    }

    return attachmentUploads;
  }

  /**
   * Prepares attachments as File objects for form submission
   * @returns Promise resolving to record of File objects organized by identifier
   */
  public async prepareAttachmentsAsFiles(): Promise<Record<string, File[]>> {
    const attachmentData = await this.prepareAttachmentsForSubmission();
    const fileRecords: Record<string, File[]> = {};
    
    for (const [identifier, attachmentArray] of Object.entries(attachmentData)) {
      fileRecords[identifier] = attachmentArray.map(attachment => attachmentDataToFile(attachment));
    }
    
    return fileRecords;
  }

  /**
   * Prepares signatures for form submission by extracting base64 data
   * @returns Promise resolving to record of signature data organized by identifier
   */
  public async prepareSignaturesForSubmission(): Promise<Record<string, AttachmentDataForSubmission[]>> {
    const signatures = this.signatureService.signatures();
    const signatureData: Record<string, AttachmentDataForSubmission[]> = {};
    
    for (const [identifier, signatureArray] of Object.entries(signatures)) {
      signatureData[identifier] = signatureArray;
    }
    
    return signatureData;
  }

  /**
   * Prepares signatures as File objects for form submission
   * @returns Promise resolving to record of File objects organized by identifier
   */
  public async prepareSignaturesAsFiles(): Promise<Record<string, File[]>> {
    const signatureData = await this.prepareSignaturesForSubmission();
    const fileRecords: Record<string, File[]> = {};
    
    for (const [identifier, signatureArray] of Object.entries(signatureData)) {
      fileRecords[identifier] = signatureArray.map(signature => attachmentDataToFile(signature));
    }
    
    return fileRecords;
  }

  /**
   * Legacy method for handling file list updates
   * Processes multiple files and adds them to the default attachment group
   * @param files - FileList of files to process
   */
  async onAttachmentsUpdated(files: FileList): Promise<void> {
    const newAttachments: AttachmentUploadData[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      if (!this.isValidFileType(file)) {
        this.toastService.showWarningMessage(`${file.name} is not a supported file type.`);
        continue;
      }

      if (!this.isValidFileSize(file)) {
        const category = this.getFileCategory(file);
        const maxSize = category === 'image' ? this.MAX_IMAGE_SIZE : 
                       category === 'video' ? this.MAX_VIDEO_SIZE : 
                       this.MAX_DOCUMENT_SIZE;
        
        this.toastService.showWarningMessage(
          `${file.name} is too large. Maximum size for ${category}s is ${this.formatFileSize(maxSize)}.`
        );
        continue;
      }

      const attachmentData: AttachmentUploadData = {
        id: crypto.randomUUID(),
        url: URL.createObjectURL(file),
        name: file.name,
        type: file.type,
        size: file.size,
        uploadDate: new Date()
      };
      newAttachments.push(attachmentData);
    }

    this._attachments.update(current => {
      const identifier = 'default';
      return {
        ...current,
        [identifier]: [...(current[identifier] || []), ...newAttachments]
      };
    });
  }

  /**
   * Opens the attachment viewer modal for a specific attachment
   * @param attachment - The attachment data to display in the modal
   */
  async openAttachmentViewer(attachment: AttachmentUploadData): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: AttachmentViewerModalComponent,
      componentProps: {
        attachment
      }
    });
    await modal.present();
  }

  /**
   * Returns the appropriate Ionic icon name based on file type
   * @param attachment - The attachment to get an icon for
   * @returns String representing the Ionic icon name
   */
  public getFileIcon(attachment: AttachmentUploadData): string {
    if (attachment.type.startsWith('image/')) {
      return 'image-outline';
    } else if (attachment.type.startsWith('video/')) {
      return 'videocam-outline';
    } else if (attachment.type === 'application/pdf') {
      return 'document-outline';
    } else if (attachment.type.includes('word')) {
      return 'document-text-outline';
    } else {
      return 'attach-outline';
    }
  }

  /**
   * Determines if a file can be previewed in the application
   * @param attachment - The attachment to check
   * @returns True if the file can be previewed, false otherwise
   */
  public canPreview(attachment: AttachmentUploadData): boolean {
    return attachment.type.startsWith('image/') || 
           attachment.type.startsWith('video/') || 
           attachment.type === 'application/pdf';
  }
}